import axios, {CancelToken, CancelTokenSource, CancelTokenStatic} from 'axios';
import moment from 'moment';
import {MutableRefObject, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {CancelRequestError} from '../enums/cancel-request-error';
import {getCalendar} from '../i18n/calendars';
import {translations} from '../i18n/translations';
import {SectionListData} from '../interfaces/section-list-data';

// Hold variables
const BATCH_SIZE: number = 10;
const THRESHOLD: number = 0.9;

export const useInfiniteLoading = (params: {
  type: 'standard' | 'section-calendar' | 'section-new-old-f' | 'section-new-old-m';
  handleFetchData: (
    limit: number,
    skip: number,
    cancelToken?: CancelToken
  ) => Promise<{
    totalCount: number;
    data: Array<{
      id: string;
      item: any;
      date?: string;
    }>;
  }>;
}) => {
  // Hold state
  const [data, setData] = useState<Array<any>>([]);
  const [sectionListData, setSectionListData] = useState<SectionListData<any>>({});
  const [itemsId, setItemsId] = useState<Array<string>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [scrollY, setScrollY] = useState<number>(0);
  const [cancelSourceToken, setCancelSourceToken] = useState<CancelTokenSource>();
  const [allLoaded, setAllLoaded] = useState<boolean>(false);

  // Hold ref
  const firstLoadDoneRef: MutableRefObject<boolean> = useRef(false);
  // Note: We have this to avoid an infinite loop of fetching data if for some
  // reason an error occurs and the content never fits the screen but allLoaded is not set.
  const fetchCountWhenContentSmallRef: MutableRefObject<number> = useRef(0);

  // Translations
  const {t} = useTranslation();

  // Fetch items
  useEffect(() => {
    // Get first batch of items
    if (!firstLoadDoneRef.current) {
      // Set ref
      firstLoadDoneRef.current = true;

      // Log
      console.debug('ℹ️ We will try to fetch the first batch of items.');

      // Refresh data
      handleRefresh();
    } else if (
      document.documentElement.scrollHeight <= document.documentElement.clientHeight &&
      !allLoaded &&
      firstLoadDoneRef.current &&
      fetchCountWhenContentSmallRef.current < 5
    ) {
      // Fetch more data if the content can't be scrolled and the data was not all loaded yet.
      // Note: When the data can be scrolled, the listener below will be used.

      // Log
      console.debug("ℹ️ The content doesn't fit the screen. We will try to fetch more items.");

      // Get more
      handleGetItems(false, true);
    }

    // Scroll listener
    const scrollListener = () => {
      // Current value for vertical scroll
      const currentScrollY = document.documentElement.scrollTop;

      // Check if 90% (THRESHOLD) of the data was seen by the user
      // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight
      const threshold: boolean =
        document.documentElement.clientHeight + currentScrollY >=
        THRESHOLD * document.documentElement.scrollHeight;

      // Load more items
      if (
        // The user is scrolling down
        currentScrollY > scrollY &&
        // And has seen 90% of the data
        threshold
      ) {
        // Log
        console.debug('ℹ️ Threshold passed. We will try to fetch more items.');

        // Get more
        handleGetItems();
      }

      // Update scrollY
      setScrollY(currentScrollY);
    };

    // Listen for scroll changes
    // About passive: https://blog.chromium.org/2016/05/new-apis-to-help-developers-improve.html
    window.addEventListener('scroll', scrollListener, {passive: true});

    // Clean up
    return () => {
      // Remove listener
      window.removeEventListener('scroll', scrollListener);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, scrollY]);

  const handleRefresh = async () => {
    // Cancel any current request
    if (cancelSourceToken && isLoading) {
      // Cancel it
      cancelSourceToken.cancel(CancelRequestError.CancelToken);
    }

    // Clear data
    setData([]);
    setItemsId([]);
    setSectionListData({});

    // Refresh data
    await handleGetItems(true);
  };

  const handleGetItems = async (refresh: boolean = false, whenContentSmall: boolean = false) => {
    // Check if the user can get more items
    if (isLoading && !refresh) {
      // Log
      console.debug("🛑 Already fetching items. We won't fetch more.");

      return;
    } else if (allLoaded && !refresh) {
      // Log
      console.debug("🛑 All data was loaded. We won't fetch more.");

      return;
    } else {
      // Log
      console.debug('🟢 Fetching items. Refreshing:', refresh);

      // Update
      if (whenContentSmall) {
        fetchCountWhenContentSmallRef.current += 1;
      }
    }

    // Start loading
    setIsLoading(true);

    // Cancel token to cancel requests
    const cancelToken: CancelTokenStatic = axios.CancelToken;
    const source: CancelTokenSource = cancelToken.source();

    // Set the new cancel token
    setCancelSourceToken(source);

    // New data
    let newData: Array<any> = [];
    let newItemsId: Array<string> = [];
    let newSectionListData: SectionListData<any> = {};

    // No refresh?
    if (!refresh) {
      // Keep current values when the user is not refreshing
      newItemsId = [...itemsId];
      if (
        params.type === 'section-calendar' ||
        params.type === 'section-new-old-f' ||
        params.type === 'section-new-old-m'
      ) {
        newSectionListData = {...sectionListData};
      } else {
        newData = [...data];
      }
    } else {
      // Reset all loaded variable
      setAllLoaded(false);
    }

    try {
      // Fetch new data
      const results = await params.handleFetchData(BATCH_SIZE, newItemsId.length, source.token);

      // Add results without repeating items
      for (const newItem of results.data) {
        if (!newItemsId.includes(newItem.id)) {
          // Add new index
          newItemsId.push(newItem.id);

          // Add new item
          if (params.type === 'section-calendar') {
            // If date is available
            if (newItem.date) {
              // Set date
              const dateAt = moment(newItem.date);

              // Get section name for the date
              const sectionName: string = dateAt.calendar(
                null,
                getCalendar(dateAt, moment.locale())
              );

              // Check if the section already exits
              if (!newSectionListData[sectionName]) {
                // Create section
                newSectionListData[sectionName] = [];
              }

              // Push item
              newSectionListData[sectionName].push(newItem.item);
            }
          } else if (params.type === 'section-new-old-f' || params.type === 'section-new-old-m') {
            // Today
            const today = moment().startOf('day');
            const happenedToday: boolean = newItem.date
              ? moment(newItem.date).startOf('day').isSame(today)
                ? true
                : false
              : false;

            // Old section name
            const oldSectionName: string =
              params.type === 'section-new-old-f'
                ? t(translations.section_list.old_f)
                : t(translations.section_list.old_m);

            // Get section name for the date
            const sectionName: string = happenedToday
              ? t(translations.section_list.today)
              : oldSectionName;

            // Check if the section already exits
            if (!newSectionListData[sectionName]) {
              // Create section
              newSectionListData[sectionName] = [];
            }

            // Push item
            newSectionListData[sectionName].push(newItem.item);
          } else {
            // Push item
            newData.push(newItem.item);
          }
        }
      }

      // Update data
      setItemsId(newItemsId);
      if (
        params.type === 'section-calendar' ||
        params.type === 'section-new-old-f' ||
        params.type === 'section-new-old-m'
      ) {
        setSectionListData(newSectionListData);
      } else {
        setData(newData);
      }

      // Get total items
      let totalItems: number = 0;
      if (
        params.type === 'section-calendar' ||
        params.type === 'section-new-old-f' ||
        params.type === 'section-new-old-m'
      ) {
        for (const [, value] of Object.entries(newSectionListData)) {
          totalItems += value.length;
        }
      } else {
        totalItems = newData.length;
      }

      // Set all loaded
      if (!allLoaded && (results.totalCount === 0 || !(totalItems < results.totalCount))) {
        setAllLoaded(true);
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.debug('🔴 Could not fetch items.', error);
      }
    } finally {
      // Always stop loading, including when the fetch function rejects.
      setIsLoading(false);
    }
  };

  // Update a property for all items in section list
  const updateAllSectionListItemsForProperty = (key: string, value: any) => {
    // New data
    let newSectionListData: SectionListData<any> = {};

    // Process
    for (const [section, data] of Object.entries(sectionListData)) {
      newSectionListData[section] = data.map((obj) => {
        return {
          ...obj,
          [key]: value,
        };
      });
    }

    // Set
    setSectionListData(newSectionListData);
  };

  return {
    isLoading,
    handleRefresh,
    data,
    sectionListData,
    allLoaded,
    updateAllSectionListItemsForProperty,
  };
};
